import { AuthError, Session, SupabaseClient } from "@supabase/supabase-js"
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { supabase } from "./client"
import { Database } from "./database.types"
import { identifyUser, resetUser } from "@/lib/posthog"
import { resolveUserOrganization } from "@/components/session/session-guards"
import { invalidateSessionCache } from "@/components/session/session-guards"
import { invalidateUserCache } from "@/components/session/user-service"

// Deliberate sign-out flows (session bouncer, /sign-out) claim the post-sign-out
// redirect BEFORE calling supabase.auth.signOut(): the SIGNED_OUT event fires
// during that call — before the app's own navigation — so a pathname check alone
// would always lose the race and clobber params like expired=true.
let signOutRedirectClaimed = false

export const claimSignOutRedirect = () => {
  signOutRedirectClaimed = true
}

const consumeSignOutRedirectClaim = () => {
  const claimed = signOutRedirectClaimed
  signOutRedirectClaimed = false
  return claimed
}

export type SessionContext =
  | {
      isLoading: true
      session: null
      error: null
      supabaseClient: SupabaseClient<Database>
    }
  | {
      isLoading: false
      session: Session
      error: null
      supabaseClient: SupabaseClient<Database>
    }
  | {
      isLoading: false
      session: null
      error: AuthError
      supabaseClient: SupabaseClient<Database>
    }
  | {
      isLoading: false
      session: null
      error: null
      supabaseClient: SupabaseClient<Database>
    }

const SessionContext = createContext<SessionContext>({
  isLoading: true,
  session: null,
  error: null,
  supabaseClient: supabase,
})

export interface SessionContextProviderProps {
  initialSession?: Session | null
  children: React.ReactNode
}

export const SessionContextProvider = ({
  initialSession = null,
  children,
}: SessionContextProviderProps) => {
  const [session, setSession] = useState<Session | null>(initialSession)
  const [isLoading, setIsLoading] = useState<boolean>(!initialSession)
  const [error, setError] = useState<AuthError | null>(null)

  useEffect(() => {
    let mounted = true

    async function getSession() {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession()

      // only update the react state if the component is still mounted
      if (mounted) {
        if (error) {
          setError(error)
          setIsLoading(false)
          return
        }

        setSession(session)
        setIsLoading(false)
      }
    }

    getSession()

    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
        invalidateUserCache()
        invalidateSessionCache()
      }

      if (
        session &&
        (event === "SIGNED_IN" ||
          event === "TOKEN_REFRESHED" ||
          event === "USER_UPDATED")
      ) {
        setSession(session)

        // Import from auth-service to avoid circular dependencies
        import("@/components/auth/auth-service").then(
          ({ handleAuthStateChange }) => {
            // Handle invite tokens and organization assignment
            handleAuthStateChange(
              event,
              session,
              () => {
                // Don't navigate here - organization assignment completed
                // The user will be redirected by the component that initiated the auth flow
              },
              (error) => {
                console.error("Failed to process invite:", error)
                // Optional: show error message to user
                // We could add a toast notification here
              },
            )
          },
        )
      }

      if (event === "SIGNED_OUT") {
        setSession(null)
        // A session can die WITHOUT a navigation — a background token-refresh
        // failure (common in incognito, where timers are throttled, or when the
        // refresh token itself has expired). Loader-level requireAuth only fires
        // on navigation, so otherwise a dead session lingers on the page and any
        // mutation runs as the anon role: org-scoped RPCs then raise misleading
        // errors ("audit not found or cross-org") and toggles silently no-op.
        // Bounce to sign-in proactively, preserving the return path. A hard
        // reload (not SPA navigate) also clears stale in-memory state. Guard the
        // sign-in route to avoid a redirect loop, and yield to deliberate
        // sign-out flows that claimed the redirect (they navigate themselves).
        const claimed = consumeSignOutRedirectClaim()
        if (
          !claimed &&
          typeof window !== "undefined" &&
          !window.location.pathname.startsWith("/sign-in")
        ) {
          const here = window.location.pathname + window.location.search
          window.location.assign(`/sign-in?redirect=${encodeURIComponent(here)}`)
        }
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // PostHog identity wiring (codex finding #2 — flag targeting requires identify).
  // Identify immediately with user.id + email so flags can resolve; then fetch organization_id
  // and re-identify (PostHog merges props). Reset on sign-out.
  useEffect(() => {
    const userId = session?.user?.id
    if (!userId) {
      resetUser()
      return
    }

    identifyUser(userId, { email: session.user.email })

    let cancelled = false
    // Optional org enrichment for staff-targeting feature flags. Best-effort: never throws.
    //
    // Resolves through resolveUserOrganization rather than reading users.organization_id
    // directly, so it stays a true mirror of requireSessionAndOrg() and my_org_id(). For
    // an internal_user acting as an organization the effective org is the acting
    // session's; users.organization_id is NULL for them and the database ignores it
    // anyway. Reading the column directly would leave an acting user identified with no
    // org, and case-builder-flow gates the whole builder shell — so they would be shown a
    // different product than the customer whose case they are configuring, which is
    // exactly what acting-as is supposed to rule out.
    try {
      resolveUserOrganization(session).then(
        ({ organizationId }) => {
          if (cancelled) return
          // Identify even when organizationId is null, passing null explicitly rather
          // than returning early. PostHog MERGES properties, so an early return would
          // leave a previously-set organization_id attached to this identity — after
          // internal staff exit an acting session, org-targeted flags would keep
          // evaluating as the customer they just left.
          identifyUser(userId, {
            email: session.user.email,
            organization_id: organizationId,
          })
        },
        () => {
          // Silent — identify already fired with email. org is enrichment only.
        },
      )
    } catch {
      // Test environments may not stub supabase.from; identify already fired with email.
    }

    return () => {
      cancelled = true
    }
    // Depends on the whole session because resolveUserOrganization needs it (it reads
    // user.id and the legacy user_metadata.org_id fallback). Broader than the previous
    // [id, email] pair, so this also re-runs when the session object is replaced — e.g.
    // a token refresh. That is harmless: identify merges props and reset only fires when
    // there is no user, so a re-run re-asserts the same identity.
  }, [session])

  const value: SessionContext = useMemo(() => {
    if (isLoading) {
      return {
        isLoading: true,
        session: null,
        error: null,
        supabaseClient: supabase,
      }
    }

    if (error) {
      return {
        isLoading: false,
        session: null,
        error,
        supabaseClient: supabase,
      }
    }

    return {
      isLoading: false,
      session,
      error: null,
      supabaseClient: supabase,
    }
  }, [isLoading, session, error])

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  )
}

export const useSessionContext = () => useContext(SessionContext)

export function useSupabaseClient<
  SchemaName extends string & keyof Database = "public" extends keyof Database
    ? "public"
    : string & keyof Database,
>() {
  return useContext(SessionContext).supabaseClient as unknown as SupabaseClient<
    Database,
    SchemaName
  >
}

export const useSession = () => useContext(SessionContext).session

export const useUser = () => useContext(SessionContext).session?.user ?? null
